import React, { useEffect } from 'react';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { Line, Bar, Doughnut, Chart } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge } from 'reactstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

import '../Admin.css';
import { getCssVar } from '../../../utils/lottieColorUtils';
import { getAdminDashboard, getAgentHealthOnly } from '../../../store/Admin';
import { ApiUrl } from '../../../store/config';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const AdminDashboard = ({ accessType, workspaceId }) => {
  const dispatch = useDispatch();
  const { adminDashboard, loading } = useSelector(state => state['Admin']);
  const primaryColor = useSelector(state => state['Settings']?.['currentBranding']?.['primaryColor']);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState(new Date());

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    // Only refresh agent health, not the entire dashboard
    await dispatch(getAgentHealthOnly());
    setLastRefreshed(new Date());
    // Wait 5 seconds to ensure proper status checking
    setTimeout(() => setIsRefreshing(false), 5000);
  };

  useEffect(() => {
    dispatch(getAdminDashboard());
    setLastRefreshed(new Date());
    // No auto-refresh - manual refresh only
  }, []);

  const getStatusBadge = (status) => {
    const statusColors = { 'healthy': 'success', 'degraded': 'warning', 'down': 'danger', 'active': 'success', 'inactive': 'danger', 'checking': 'warning' };
    const displayStatus = status === 'active' ? 'Active' : status === 'inactive' ? 'Inactive' : status === 'checking' ? 'Checking...' : status;
    return <Badge color={statusColors[status] || 'secondary'}>{displayStatus}</Badge>;
  };

  const getActivityIcon = (type) => {
    const icons = { 'user_created': 'fa-user-plus', 'user_updated': 'fa-user-edit', 'user_deleted': 'fa-user-times', 'workspace_created': 'fa-building', 'workspace_updated': 'fa-edit', 'workspace_suspended': 'fa-pause-circle', 'workspace_deleted': 'fa-trash-alt', 'agent_created': 'fa-robot', 'agent_started': 'fa-play-circle', 'agent_stopped': 'fa-stop-circle', 'agent_deleted': 'fa-trash-alt', 'system_backup': 'fa-database', 'system_update': 'fa-sync-alt', 'system_error': 'fa-exclamation-triangle', 'login_success': 'fa-sign-in-alt', 'login_failed': 'fa-times-circle', 'subscription_created': 'fa-credit-card', 'subscription_upgraded': 'fa-arrow-up', 'subscription_cancelled': 'fa-times', 'payment_success': 'fa-dollar-sign', 'payment_failed': 'fa-exclamation-circle', 'security_alert': 'fa-shield-alt', 'api_key_created': 'fa-key', 'api_key_revoked': 'fa-ban' };
    return icons[type] || 'fa-info-circle';
  };

  const getActivityColor = (severity) => {
    const colors = { 'success': 'text-success', 'info': 'text-info', 'warning': 'text-warning', 'error': 'text-danger' };
    return colors[severity] || 'text-secondary';
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(timestamp)) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
    return new Date(timestamp).toLocaleDateString();
  };


  // Extract data from Redux store
  const stats = adminDashboard || { 'userStats': { 'summary': { 'total': 0, 'active': 0, 'growth': 0 } }, 'workspaceStats': { 'summary': { 'total': 0, 'active': 0, 'growth': 0 } }, 'agentStats': { 'summary': { 'total': 0, 'active': 0, 'growth': 0 } }, 'revenueData': { 'totalRevenue': 0, 'growth': 0 }, 'systemHealth': {}, 'agentHealth': {} };

  // Extract agent health data with defaults
  const agentHealth = stats['agentHealth'] || {
    voice: { status: 'checking', responseTime: 0 },
    outbound: { status: 'checking', responseTime: 0 },
    inbound: { status: 'checking', responseTime: 0 }
  };

  // Combined chart data - Revenue and Users
  const combinedChartData = {
    labels: stats['revenueData']?.['chartData']?.['labels'] || [],
    datasets: [
      {
        label: 'Total Revenue ($)',
        data: stats['revenueData']?.['chartData']?.['revenue'] || [],
        borderColor: getCssVar('--sa-primary'),
        backgroundColor: getCssVar('--sa-primary'),
        yAxisID: 'y',
        type: 'line',
        tension: 0.1
      },
      {
        label: 'Credit Revenue ($)',
        data: stats['revenueData']?.['chartData']?.['creditRevenue'] || [],
        borderColor: '#F7931A',
        backgroundColor: '#F7931A',
        yAxisID: 'y',
        type: 'line',
        tension: 0.1
      },
      {
        label: 'Subscription Revenue ($)',
        data: stats['revenueData']?.['chartData']?.['subscriptionRevenue'] || [],
        borderColor: getCssVar('--sa-primary-soft'),
        backgroundColor: getCssVar('--sa-primary-soft'),
        yAxisID: 'y',
        type: 'line',
        tension: 0.1
      },
      {
        label: 'New Workspaces',
        data: stats['revenueData']?.['chartData']?.['userGrowth'] || [],
        borderColor: getCssVar('--sa-dark-accent'),
        backgroundColor: getCssVar('--sa-dark-accent'),
        yAxisID: 'y1',
        type: 'bar'
      }
    ]
  };

  // Get revenue breakdown by type from revenueData
  const revenueByType = stats['revenueData']?.['revenueByType'] || {};

  // Create revenue distribution for pie chart
  const revenueLabels = [];
  const revenueValues = [];
  const backgroundColors = [];

  // Add subscription revenue if exists
  if (revenueByType.subscription?.revenue > 0) {
    revenueLabels.push('Subscription Plans');
    revenueValues.push(revenueByType.subscription.revenue);
    backgroundColors.push(getCssVar('--sa-dark-accent'));
  }

  // Add credit revenue if exists
  if (revenueByType.credits?.revenue > 0) {
    revenueLabels.push('Credit Purchases');
    revenueValues.push(revenueByType.credits.revenue);
    backgroundColors.push(getCssVar('--sa-primary-soft'));
  }

  // If no revenue data, show placeholder
  if (revenueLabels.length === 0) {
    revenueLabels.push('No Revenue');
    revenueValues.push(1);
    backgroundColors.push(getCssVar('--sa-primary'));
  }

  const revenueDistribution = {
    labels: revenueLabels,
    datasets: [{
      data: revenueValues,
      backgroundColor: backgroundColors
    }]
  };

  if (loading && !adminDashboard) return <Container className="mt-4"><div className="text-center">Loading admin dashboard...</div></Container>;

  return (
    <>
      <div className='content-dashboard'>
        {/* Dashboard Header */}
        <div className="dashboard-header-bar">
          <div className="meta-row">
            <h5>Dashboard Overview</h5>
            <small className="text-muted">Last updated: {lastRefreshed.toLocaleTimeString()}</small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '10px' }}>
            {isRefreshing && (
              <span className="refresh-checking-text">
                <i className="fa fa-spinner fa-spin"></i>
                Checking agent health...
              </span>
            )}
            <button
              className={`btn btn-sm ${isRefreshing ? 'btn-warning' : 'btn-success'}`}
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              title="Refresh agent health status only"
              style={{ minWidth: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            >
              <i className={`fa ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-heartbeat'}`}></i>
              {isRefreshing ? 'Checking Agents...' : 'Refresh Agent Health'}
            </button>
          </div>
        </div>
        {/* Key Metrics */}
        <div className='counter-sections dasboard-counter'>
          <div className='counter-box counter-successfull'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>{accessType === 'agency' ? 'Sub-Workspace Users' : 'Total Users'}</h5>
                <div className='CountUp-box'>
                  <CountUp end={stats['userStats']?.['summary']?.['total'] || 0} duration={1}>
                    {({ countUpRef, start }) => (<VisibilitySensor onChange={start}><span className='CountUp' ref={countUpRef} /></VisibilitySensor>)}
                  </CountUp>
                </div>
                <div className='status-text'>
                  <small className={stats['userStats']?.['summary']?.['growth'] >= 0 ? 'text-success' : 'text-danger'}>
                    <i className={`icon`}>
                      {stats['userStats']?.['summary']?.['growth'] >= 0
                        ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                        : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                      }
                    </i>
                    {Math.abs(stats['userStats']?.['summary']?.['growth'] || 0)}%
                  </small>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#13B5EA" />
                    <path d="M14.1062 15.7467C14.0862 15.7467 14.0729 15.7467 14.0529 15.7467C14.0196 15.74 13.9729 15.74 13.9329 15.7467C11.9996 15.6867 10.5396 14.1667 10.5396 12.2933C10.5396 10.3867 12.0929 8.83333 13.9996 8.83333C15.9062 8.83333 17.4596 10.3867 17.4596 12.2933C17.4529 14.1667 15.9862 15.6867 14.1262 15.7467C14.1196 15.7467 14.1129 15.7467 14.1062 15.7467ZM13.9996 9.83333C12.6462 9.83333 11.5396 10.94 11.5396 12.2933C11.5396 13.6267 12.5796 14.7 13.9062 14.7467C13.9462 14.74 14.0329 14.74 14.1196 14.7467C15.4262 14.6867 16.4529 13.6133 16.4596 12.2933C16.4596 10.94 15.3529 9.83333 13.9996 9.83333Z" fill="#13B5EA" />
                    <path d="M19.0261 15.8333C19.0061 15.8333 18.9861 15.8333 18.9661 15.8267C18.6928 15.8533 18.4128 15.66 18.3861 15.3867C18.3594 15.1133 18.5261 14.8667 18.7994 14.8333C18.8794 14.8267 18.9661 14.8267 19.0394 14.8267C20.0128 14.7733 20.7728 13.9733 20.7728 12.9933C20.7728 11.98 19.9528 11.16 18.9394 11.16C18.6661 11.1667 18.4394 10.94 18.4394 10.6667C18.4394 10.3933 18.6661 10.1667 18.9394 10.1667C20.4994 10.1667 21.7728 11.44 21.7728 13C21.7728 14.5333 20.5728 15.7733 19.0461 15.8333C19.0394 15.8333 19.0328 15.8333 19.0261 15.8333Z" fill="#13B5EA" />
                    <path d="M14.1131 23.0333C12.8064 23.0333 11.4931 22.7 10.4997 22.0333C9.57307 21.42 9.06641 20.58 9.06641 19.6667C9.06641 18.7533 9.57307 17.9067 10.4997 17.2867C12.4997 15.96 15.7397 15.96 17.7264 17.2867C18.6464 17.9 19.1597 18.74 19.1597 19.6533C19.1597 20.5667 18.6531 21.4133 17.7264 22.0333C16.7264 22.7 15.4197 23.0333 14.1131 23.0333ZM11.0531 18.1267C10.4131 18.5533 10.0664 19.1 10.0664 19.6733C10.0664 20.24 10.4197 20.7867 11.0531 21.2067C12.7131 22.32 15.5131 22.32 17.1731 21.2067C17.8131 20.78 18.1597 20.2333 18.1597 19.66C18.1597 19.0933 17.8064 18.5467 17.1731 18.1267C15.5131 17.02 12.7131 17.02 11.0531 18.1267Z" fill="#13B5EA" />
                    <path d="M20.2263 21.8333C19.993 21.8333 19.7863 21.6733 19.7396 21.4333C19.6863 21.16 19.8596 20.9 20.1263 20.84C20.5463 20.7533 20.933 20.5867 21.233 20.3533C21.613 20.0667 21.8196 19.7067 21.8196 19.3267C21.8196 18.9467 21.613 18.5867 21.2396 18.3067C20.9463 18.08 20.5796 17.92 20.1463 17.82C19.8796 17.76 19.7063 17.4933 19.7663 17.22C19.8263 16.9533 20.093 16.78 20.3663 16.84C20.9396 16.9667 21.4396 17.1933 21.8463 17.5067C22.4663 17.9733 22.8196 18.6333 22.8196 19.3267C22.8196 20.02 22.4596 20.68 21.8396 21.1533C21.4263 21.4733 20.9063 21.7067 20.333 21.82C20.293 21.8333 20.2596 21.8333 20.2263 21.8333Z" fill="#13B5EA" />
                  </svg>

                </span>
              </div>
            </div>
          </div>
          <div className='counter-box counter-successfull'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>{accessType === 'agency' ? 'Sub-Workspaces' : 'Workspaces'}</h5>
                <div className='CountUp-box'>
                  <CountUp end={stats['workspaceStats']?.['summary']?.['total'] || 0} duration={1}>
                    {({ countUpRef, start }) => (<VisibilitySensor onChange={start}><span className='CountUp' ref={countUpRef} /></VisibilitySensor>)}
                  </CountUp>
                </div>
                <div className='status-text'>
                  <small className={stats['workspaceStats']?.['summary']?.['growth'] >= 0 ? 'text-success' : 'text-danger'}>
                    <i className={`icon`}>
                      {stats['workspaceStats']?.['summary']?.['growth'] >= 0
                        ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                        : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                      }
                    </i>
                    {Math.abs(stats['workspaceStats']?.['summary']?.['growth'] || 0)}%
                  </small>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#20808D" />
                    <path d="M22.6668 23.1667H9.3335C9.06016 23.1667 8.8335 22.94 8.8335 22.6667C8.8335 22.3933 9.06016 22.1667 9.3335 22.1667H22.6668C22.9402 22.1667 23.1668 22.3933 23.1668 22.6667C23.1668 22.94 22.9402 23.1667 22.6668 23.1667Z" fill="#20808D" />
                    <path d="M22 23.1667H10C9.72667 23.1667 9.5 22.94 9.5 22.6667V12C9.5 9.98666 10.6533 8.83333 12.6667 8.83333H19.3333C21.3467 8.83333 22.5 9.98666 22.5 12V22.6667C22.5 22.94 22.2733 23.1667 22 23.1667ZM10.5 22.1667H21.5V12C21.5 10.52 20.8133 9.83333 19.3333 9.83333H12.6667C11.1867 9.83333 10.5 10.52 10.5 12V22.1667Z" fill="#20808D" />
                    <path d="M14.6665 19.5H12.6665C12.3932 19.5 12.1665 19.2733 12.1665 19C12.1665 18.7267 12.3932 18.5 12.6665 18.5H14.6665C14.9398 18.5 15.1665 18.7267 15.1665 19C15.1665 19.2733 14.9398 19.5 14.6665 19.5Z" fill="#20808D" />
                    <path d="M19.3335 19.5H17.3335C17.0602 19.5 16.8335 19.2733 16.8335 19C16.8335 18.7267 17.0602 18.5 17.3335 18.5H19.3335C19.6068 18.5 19.8335 18.7267 19.8335 19C19.8335 19.2733 19.6068 19.5 19.3335 19.5Z" fill="#20808D" />
                    <path d="M14.6665 16.5H12.6665C12.3932 16.5 12.1665 16.2733 12.1665 16C12.1665 15.7267 12.3932 15.5 12.6665 15.5H14.6665C14.9398 15.5 15.1665 15.7267 15.1665 16C15.1665 16.2733 14.9398 16.5 14.6665 16.5Z" fill="#20808D" />
                    <path d="M19.3335 16.5H17.3335C17.0602 16.5 16.8335 16.2733 16.8335 16C16.8335 15.7267 17.0602 15.5 17.3335 15.5H19.3335C19.6068 15.5 19.8335 15.7267 19.8335 16C19.8335 16.2733 19.6068 16.5 19.3335 16.5Z" fill="#20808D" />
                    <path d="M14.6665 13.5H12.6665C12.3932 13.5 12.1665 13.2733 12.1665 13C12.1665 12.7267 12.3932 12.5 12.6665 12.5H14.6665C14.9398 12.5 15.1665 12.7267 15.1665 13C15.1665 13.2733 14.9398 13.5 14.6665 13.5Z" fill="#20808D" />
                    <path d="M19.3335 13.5H17.3335C17.0602 13.5 16.8335 13.2733 16.8335 13C16.8335 12.7267 17.0602 12.5 17.3335 12.5H19.3335C19.6068 12.5 19.8335 12.7267 19.8335 13C19.8335 13.2733 19.6068 13.5 19.3335 13.5Z" fill="#20808D" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          
          <div className='counter-box counter-successfull'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Total Credits Allocated</h5>
                <div className='CountUp-box'>
                  <CountUp end={stats['revenueData']?.['totalMonthlyCredits'] || 0}>
                    {({ countUpRef, start }) => (<VisibilitySensor onChange={start} ><span className='CountUp' ref={countUpRef} /></VisibilitySensor>)}
                  </CountUp>
                </div>
                <div className='subscription-details'>
                  <small className='text-muted'>Total monthly credits across all plans</small>
                </div>
                <div className='status-text'>
                  <small className={stats['revenueData']?.['growth'] >= 0 ? 'text-success' : 'text-danger'}>
                    <i className={`icon`}>
                      {stats['revenueData']?.['growth'] >= 0
                        ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                        : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                      }
                    </i>
                    {Math.abs(stats['revenueData']?.['growth'] || 0)}%
                  </small>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#9333EA" />
                    <path d="M15.9989 23.1667C13.3322 23.1667 11.1655 21.2533 11.1655 18.9V16.4333C11.1655 16.16 11.3922 15.9333 11.6655 15.9333C11.9389 15.9333 12.1655 16.16 12.1655 16.4333C12.1655 18.18 13.8122 19.5 15.9989 19.5C18.1855 19.5 19.8322 18.18 19.8322 16.4333C19.8322 16.16 20.0589 15.9333 20.3322 15.9333C20.6055 15.9333 20.8322 16.16 20.8322 16.4333V18.9C20.8322 21.2533 18.6655 23.1667 15.9989 23.1667ZM12.1655 18.9733C12.2122 20.74 13.9122 22.1667 15.9989 22.1667C18.0855 22.1667 19.7855 20.74 19.8322 18.9733C18.9655 19.9133 17.5922 20.5 15.9989 20.5C14.4055 20.5 13.0389 19.9133 12.1655 18.9733Z" fill="#9333EA" />
                    <path d="M15.9989 17.1667C14.1589 17.1667 12.5055 16.34 11.6989 15.0067C11.3522 14.44 11.1655 13.78 11.1655 13.1C11.1655 11.9533 11.6789 10.8733 12.6055 10.06C13.5122 9.26666 14.7189 8.83333 15.9989 8.83333C17.2789 8.83333 18.4789 9.26666 19.3922 10.0533C20.3189 10.8733 20.8322 11.9533 20.8322 13.1C20.8322 13.78 20.6455 14.4333 20.2989 15.0067C19.4922 16.34 17.8389 17.1667 15.9989 17.1667ZM15.9989 9.83333C14.9589 9.83333 13.9855 10.18 13.2589 10.82C12.5522 11.4333 12.1655 12.2467 12.1655 13.1C12.1655 13.6 12.2989 14.0667 12.5522 14.4867C13.1855 15.5267 14.5055 16.1667 15.9989 16.1667C17.4922 16.1667 18.8122 15.52 19.4455 14.4867C19.7055 14.0667 19.8322 13.6 19.8322 13.1C19.8322 12.2467 19.4455 11.4333 18.7322 10.8067C18.0055 10.18 17.0389 9.83333 15.9989 9.83333Z" fill="#9333EA" />
                    <path d="M15.9989 20.5C13.2455 20.5 11.1655 18.7533 11.1655 16.4333V13.1C11.1655 10.7467 13.3322 8.83333 15.9989 8.83333C17.2789 8.83333 18.4789 9.26666 19.3922 10.0533C20.3189 10.8733 20.8322 11.9533 20.8322 13.1V16.4333C20.8322 18.7533 18.7522 20.5 15.9989 20.5ZM15.9989 9.83333C13.8855 9.83333 12.1655 11.3 12.1655 13.1V16.4333C12.1655 18.18 13.8122 19.5 15.9989 19.5C18.1855 19.5 19.8322 18.18 19.8322 16.4333V13.1C19.8322 12.2467 19.4455 11.4333 18.7322 10.8067C18.0055 10.18 17.0389 9.83333 15.9989 9.83333Z" fill="#9333EA" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className='counter-box counter-successfull'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Current Month Revenue</h5>
                <div className='CountUp-box'>
                  $
                  <CountUp end={stats['revenueData']?.['planBasedSubscriptionValue'] || 0}>
                    {({ countUpRef, start }) => (<VisibilitySensor onChange={start} ><span className='CountUp' ref={countUpRef} /></VisibilitySensor>)}
                  </CountUp>
                </div>
                <div className='subscription-details'>
                  <small className='text-muted'>
                    {stats['revenueData']?.['activeSubscriptionsCount'] || 0} active plans • Avg ${stats['revenueData']?.['avgSubscriptionValue'] || 0}/mo
                  </small>
                </div>
                <div className='status-text'>
                  <small className={stats['revenueData']?.['growth'] >= 0 ? 'text-success' : 'text-danger'}>
                    <i className={`icon`}>
                      {stats['revenueData']?.['growth'] >= 0
                        ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                        : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                      }
                    </i>
                    {Math.abs(stats['revenueData']?.['growth'] || 0)}%
                  </small>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon text-success'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#FF7A59" />
                    <path d="M15.9989 23.1667C15.5989 23.1667 15.2055 23.0734 14.8855 22.8934L11.7322 21.0734C10.4255 20.1934 10.3389 20.0334 10.3389 18.6534V15.3467C10.3389 13.9667 10.4255 13.8067 11.7055 12.9467L14.8922 11.1067C15.5255 10.74 16.4789 10.74 17.1122 11.1067L20.2655 12.9267C21.5722 13.8067 21.6589 13.9667 21.6589 15.3467V18.6534C21.6589 20.0334 21.5722 20.1934 20.2922 21.0534L17.1055 22.8934C16.7922 23.08 16.3922 23.1667 15.9989 23.1667ZM15.9989 11.8334C15.7722 11.8334 15.5455 11.88 15.3855 11.9734L12.2322 13.7934C11.3389 14.4 11.3389 14.4 11.3389 15.3467V18.6534C11.3389 19.6 11.3389 19.6 12.2655 20.2267L15.3922 22.0267C15.7122 22.2134 16.2922 22.2134 16.6122 22.0267L19.7655 20.2067C20.6589 19.6 20.6589 19.6 20.6589 18.6534V15.3467C20.6589 14.4 20.6589 14.4 19.7322 13.7734L16.6055 11.9734C16.4522 11.88 16.2255 11.8334 15.9989 11.8334Z" fill="#FF7A59" />
                    <path d="M19.6654 13.5867C19.392 13.5867 19.1654 13.36 19.1654 13.0867V11.3333C19.1654 10.28 18.7187 9.83334 17.6654 9.83334H14.332C13.2787 9.83334 12.832 10.28 12.832 11.3333V13.04C12.832 13.3133 12.6054 13.54 12.332 13.54C12.0587 13.54 11.832 13.32 11.832 13.04V11.3333C11.832 9.72001 12.7187 8.83334 14.332 8.83334H17.6654C19.2787 8.83334 20.1654 9.72001 20.1654 11.3333V13.0867C20.1654 13.36 19.9387 13.5867 19.6654 13.5867Z" fill="#FF7A59" />
                    <path d="M17.0855 19.7534C16.9455 19.7534 16.7989 19.7267 16.6522 19.6667L15.9989 19.4134L15.3455 19.6734C14.9922 19.8134 14.6322 19.7801 14.3655 19.5867C14.0989 19.3934 13.9589 19.0601 13.9789 18.6801L14.0189 17.9801L13.5722 17.4401C13.3322 17.1401 13.2522 16.7934 13.3589 16.4734C13.4589 16.1601 13.7322 15.9201 14.0989 15.8267L14.7789 15.6534L15.1589 15.0601C15.5655 14.4201 16.4389 14.4201 16.8455 15.0601L17.2255 15.6534L17.9055 15.8267C18.2722 15.9201 18.5455 16.1601 18.6455 16.4734C18.7455 16.7867 18.6655 17.1401 18.4255 17.4334L17.9789 17.9734L18.0189 18.6734C18.0389 19.0534 17.8989 19.3801 17.6322 19.5801C17.4722 19.6934 17.2855 19.7534 17.0855 19.7534ZM14.3455 16.8001L14.7922 17.3401C14.9455 17.5201 15.0322 17.8067 15.0189 18.0401L14.9789 18.7401L15.6322 18.4801C15.8522 18.3934 16.1455 18.3934 16.3655 18.4801L17.0189 18.7401L16.9789 18.0401C16.9655 17.8067 17.0522 17.5267 17.2055 17.3401L17.6522 16.8001L16.9722 16.6267C16.7455 16.5667 16.5055 16.3934 16.3789 16.2001L16.0055 15.6134L15.6255 16.2001C15.4989 16.4001 15.2589 16.5734 15.0322 16.6334L14.3455 16.8001Z" fill="#FF7A59" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <div className='counter-box counter-successfull'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Total Revenue</h5>
                <div className='CountUp-box'>
                  $
                  <CountUp end={stats['revenueData']?.['totalRevenue'] || 0}>
                    {({ countUpRef, start }) => (<VisibilitySensor onChange={start} ><span className='CountUp' ref={countUpRef} /></VisibilitySensor>)}
                  </CountUp>
                </div>
                <div className='subscription-details'>
                  <small className='text-muted'>All-time collected revenue</small>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon text-success'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#DF9524" />
                    <path d="M16.9334 19.6133H15.2601C14.1668 19.6133 13.2801 18.6933 13.2801 17.56C13.2801 17.2867 13.5068 17.06 13.7801 17.06C14.0534 17.06 14.2801 17.2867 14.2801 17.56C14.2801 18.14 14.7201 18.6133 15.2601 18.6133H16.9334C17.3668 18.6133 17.7268 18.2267 17.7268 17.76C17.7268 17.18 17.5201 17.0667 17.1801 16.9467L14.4934 16C13.9734 15.82 13.2734 15.4333 13.2734 14.24C13.2734 13.2133 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.44C18.7201 14.7133 18.4934 14.94 18.2201 14.94C17.9468 14.94 17.7201 14.7133 17.7201 14.44C17.7201 13.86 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7733 14.2734 14.24C14.2734 14.82 14.4801 14.9333 14.8201 15.0533L17.5068 16C18.0268 16.18 18.7268 16.5667 18.7268 17.76C18.7201 18.78 17.9201 19.6133 16.9334 19.6133Z" fill="#DF9524" />
                    <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#DF9524" />
                    <path d="M16.0002 23.1667C12.0468 23.1667 8.8335 19.9533 8.8335 16C8.8335 12.0467 12.0468 8.83333 16.0002 8.83333C19.9535 8.83333 23.1668 12.0467 23.1668 16C23.1668 19.9533 19.9535 23.1667 16.0002 23.1667ZM16.0002 9.83333C12.6002 9.83333 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1667 16.0002 22.1667C19.4002 22.1667 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83333 16.0002 9.83333Z" fill="#DF9524" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className='chart-box'>
          <div className='row'>
            {/* Row 1: Revenue chart (left) + Agent Health (right) */}
            <div className='col-lg-8 col-md-12'>
              <div className='chart-area'>
                <h4>Revenue &amp; Workspace Growth Trends</h4>
                <div style={{ height: '240px' }}>
                  <Chart
                    type='bar'
                    data={combinedChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: { display: false, ticks: { color: getCssVar('--sa-dark-text-muted') } },
                        y: {
                          type: 'linear', display: true, position: 'left', beginAtZero: true,
                          title: { display: true, text: 'Revenue ($)', color: getCssVar('--sa-dark-text-muted') },
                          ticks: { color: getCssVar('--sa-dark-text-muted'), callback: v => '$' + v.toLocaleString() },
                          grid: { color: getCssVar('--sa-dark-border') }
                        },
                        y1: {
                          type: 'linear', display: true, position: 'right', beginAtZero: true,
                          title: { display: true, text: 'New Workspaces', color: getCssVar('--sa-dark-text-muted') },
                          ticks: { color: getCssVar('--sa-dark-text-muted') },
                          grid: { drawOnChartArea: false, color: getCssVar('--sa-dark-border') }
                        }
                      },
                      plugins: {
                        legend: { labels: { color: getCssVar('--sa-dark-text-muted'), boxWidth: 12, font: { size: 11 } } },
                        tooltip: {
                          callbacks: {
                            label: ctx => {
                              let lbl = ctx.dataset.label ? ctx.dataset.label + ': ' : '';
                              if (ctx.parsed.y !== null) lbl += ctx.dataset.yAxisID === 'y' ? '$' + ctx.parsed.y.toLocaleString() : ctx.parsed.y + ' Workspaces';
                              return lbl;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className='col-lg-4 col-md-12'>
              <div className='chart-area agent-health-card'>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0" style={{ borderBottom: 'none', paddingBottom: 0 }}>Agent Health</h4>
                  {isRefreshing && <small style={{ color: 'var(--primary-color)', fontSize: '11px' }}><i className="fa fa-spinner fa-spin mr-1"></i>Updating...</small>}
                </div>
                <div style={{ borderBottom: '1px solid var(--sa-dark-border)', marginBottom: '14px', marginTop: '10px' }}></div>
                <div className='system-area agent-area-system'>
                  {[
                    { key: 'voice', label: 'Voice Agent' },
                    { key: 'outbound', label: 'Outbound Agent' },
                    { key: 'inbound', label: 'Inbound Agent' }
                  ].map(({ key, label }) => (
                    <div key={key} className="d-flex justify-content-between align-items-center healty-box" style={{ width: '100%', flexDirection: 'row' }}>
                      <div className="flex-grow-1">
                        <h6 className="mb-0">{label}</h6>
                        <small className="text-muted">{agentHealth[key]?.responseTime || 0}ms</small>
                        {agentHealth[key]?.status === 'active' && agentHealth[key]?.activeJobs > 0 && (
                          <small className="text-info d-block"><i className="fa fa-briefcase mr-1"></i>{agentHealth[key].activeJobs} jobs</small>
                        )}
                        {agentHealth[key]?.status === 'inactive' && <small className="text-muted d-block">Offline</small>}
                      </div>
                      <div>{getStatusBadge(agentHealth[key]?.status || 'checking')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2: Revenue Distribution (left) + Recent Activity (right) */}
            <div className='col-lg-4 col-md-12'>
              <div className='chart-area'>
                <h4>Revenue Distribution</h4>
                <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Doughnut
                    data={revenueDistribution || { labels: [], datasets: [] }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom', labels: { color: '#a88a96', boxWidth: 12, font: { size: 11 } } },
                        tooltip: {
                          callbacks: {
                            label: ctx => (ctx.label ? ctx.label + ': ' : '') + (ctx.parsed !== null ? '$' + ctx.parsed.toLocaleString() : '')
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className='col-lg-8 col-md-12'>
              <div className='chart-area'>
                <h4>Recent Activity</h4>
                <div className="activity-list" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  {(stats['recentActivity'] && stats['recentActivity'].length > 0 ? stats['recentActivity'] : []).map(activity => {
                    const actor = activity['actor']?.['name'] || activity['actor']?.['email']?.split('@')[0] || 'System';
                    const workspace = activity['workspace']?.['name'] || activity['target']?.['name'] || '';
                    const severityColors = { success: '#4ade80', info: '#67e8f9', warning: '#fcd34d', error: '#f87171' };
                    const iconColor = severityColors[activity['severity']] || getCssVar('--sa-dark-text-muted');
                    return (
                      <div key={activity['_id']} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${getCssVar('--sa-dark-border')}` }}>
                        {/* Icon circle */}
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: getCssVar('--sa-dark-surface'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`fa ${getActivityIcon(activity['type'])}`} style={{ fontSize: '12px', color: iconColor }}></i>
                        </div>
                        {/* Text — single row layout */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                            <span style={{ fontWeight: 600, fontSize: '12px', color: '#ffffff', flexShrink: 0, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{actor}</span>
                            <span style={{ fontSize: '12px', color: getCssVar('--sa-dark-text-muted'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{activity['action']}{workspace ? ` — ${workspace}` : ''}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: getCssVar('--sa-dark-text-muted'), marginTop: '2px' }}>
                            <i className="fa fa-clock" style={{ marginRight: '4px' }}></i>
                            {getRelativeTime(activity['timestamp'])}
                            {activity['target']?.['type'] && (
                              <span style={{ marginLeft: '8px', padding: '1px 6px', background: getCssVar('--sa-dark-surface'), borderRadius: '4px', fontSize: '10px', color: getCssVar('--sa-dark-text-muted') }}>
                                {activity['target']['type']}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!stats['recentActivity'] || stats['recentActivity'].length === 0) && (
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                      <i className="fa fa-clock" style={{ fontSize: '22px', display: 'block', marginBottom: '8px', color: getCssVar('--sa-dark-text-muted') }}></i>
                      <p style={{ margin: 0, fontSize: '13px', color: getCssVar('--sa-dark-text-muted') }}>No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;